<template>
  <div class="card mb-3" data-testid="forwards-transaction-header">
    <div class="card-body">
      <div
        class="d-flex justify-content-between align-items-start gap-3 flex-wrap"
      >
        <div>
          <div class="d-flex align-items-center gap-2 mb-1">
            <span class="eyebrow-label">
              {{ $t("forwards.transaction_header.title") }}
            </span>
            <ForwardsStatusBadgeComponent
              v-if="header.is_pay_as_produced"
              status="pay_as_produced"
            />
          </div>
          <div class="fw-semibold">
            {{ $t("forwards.transaction_header.deal") }}:
            <RouterLink :to="dealPath">{{ header.deal_name }}</RouterLink>
          </div>
          <div class="text-body-tertiary fs-10">{{ windowText }}</div>
        </div>

        <div class="forwards-transaction-header__fill">
          <EscrowFillBarComponent
            :escrowed="header.escrowed_mwh"
            :total="header.planned_mwh"
            showShortfall
            :caption="
              $t('forwards.transaction_header.escrowed_of_planned', {
                escrowed: formatNumberWithSpaces(header.escrowed_mwh),
                planned: formatNumberWithSpaces(header.planned_mwh),
              })
            "
          />
        </div>
      </div>

      <div
        v-for="note in notes"
        :key="note.testId"
        class="mt-3"
        :data-testid="note.testId"
      >
        <div class="fw-semibold fs-10">{{ note.heading }}</div>
        <p class="forwards-transaction-header__notes fs-10 mb-0">
          {{ note.text }}
        </p>
      </div>

      <p class="advisory-note mt-3 mb-0">
        <FontAwesomeIcon icon="fa-solid fa-circle-info" />
        <span>{{ $t("forwards.transaction_header.managed_note") }}</span>
        <RouterLink :to="dealPath" class="ms-auto">
          {{ $t("forwards.transaction_header.open_deal") }}
        </RouterLink>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatDate, formatNumberWithSpaces } from "@/composables/formatUtils";
import EscrowFillBarComponent from "@/forwards2/components/EscrowFillBarComponent.vue";
import ForwardsStatusBadgeComponent from "@/forwards2/components/ForwardsStatusBadgeComponent.vue";
import { dealRoute } from "@/forwards2/deals/dealRoute";
import type { ForwardDeliveryHeader } from "@/forwards2/forwardsTypes";
import { computed } from "vue";
import { RouterLink } from "vue-router";
import { useI18n } from "vue-i18n";

/**
 * The forwards context of a SalesTransaction, shown above the transaction form.
 *
 * Its presence is what tells the admin that this transaction is a delivery rather than a spot sale:
 * the deal owns the items, the amounts, the buyer and the prices, and mirrors them onto the
 * transaction whenever they change (`escrow_service.mirror_delivery_transaction`). Editing them
 * here would be overwritten, so the page disables those inputs and this header says where they
 * live. Advancing the transaction's status is unaffected and stays on this page.
 */
const props = defineProps<{
  header: ForwardDeliveryHeader;
}>();

const { t } = useI18n();

const dealPath = computed(() =>
  dealRoute(props.header.deal_id, props.header.delivery_id)
);

const notes = computed(() =>
  [
    {
      testId: "forwards-transaction-header-deal-notes",
      heading: t("forwards.transaction_header.deal_notes"),
      text: props.header.deal_internal_notes,
    },
    {
      testId: "forwards-transaction-header-delivery-notes",
      heading: t("forwards.transaction_header.delivery_notes"),
      text: props.header.delivery_internal_notes,
    },
  ].filter((note) => note.text !== null)
);

/**
 * An open-ended delivery has no start: the buyer is owed the volume by `window_end` and anything
 * earlier is fine, which is what "on or before" says.
 */
const windowText = computed(() =>
  props.header.window_start === null
    ? t("forwards.transaction_header.window_open", {
        date: formatDate(props.header.window_end),
      })
    : t("forwards.transaction_header.window_range", {
        start: formatDate(props.header.window_start),
        end: formatDate(props.header.window_end),
      })
);
</script>

<style scoped lang="scss">
.forwards-transaction-header__fill {
  min-width: 16rem;
}

.forwards-transaction-header__notes {
  white-space: pre-wrap;
}
</style>
